import { FC } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { isSameDay, isSameMonth } from "date-fns";
import { useApp } from "~/core";
import { useAppTime, useTimeout } from "~/hooks";
import { useMobileDashboard } from "../../context";
import { POPUP_HEIGHT, POPUP_WIDTH } from "./Popup";
import { GridCellProps } from "./interfaces";
import cn from "classnames";

const LONG_PRESS_MS = 500;
const POPUP_OFFSET = 15;
const POPUP_X_MARGIN = 15;
const POPUP_Y_MARGIN = 65; // header + 1rem

const getPopupPosition = (rect: DOMRect, container: HTMLElement | null) => {
    const scrollTop = container?.scrollTop ?? 0;
    const offsetTop = container?.offsetTop ?? 0;

    let x = rect.right + POPUP_OFFSET;
    let y = rect.top - POPUP_HEIGHT - POPUP_OFFSET;

    if (x + POPUP_WIDTH + POPUP_X_MARGIN > window.innerWidth) {
        x = Math.max(rect.left - POPUP_WIDTH - POPUP_OFFSET, POPUP_X_MARGIN);
    }

    if (y < POPUP_Y_MARGIN) {
        y = rect.bottom + POPUP_OFFSET - offsetTop;
    }

    y += scrollTop;

    return { x, y };
};

const GridCell: FC<GridCellProps> = ({ date, isPopup, onLongPress }) => {
    const { mobile } = useApp();
    const { overviewData, selectedDay, mode } = useMobileDashboard();
    const today = useAppTime("day");
    const longPress = useTimeout(LONG_PRESS_MS);
    const clickIntent = useRef(false);

    useEffect(() => {
        const handleTouchEnd = () => {
            longPress(false);
            clickIntent.current = false;
        };

        window.addEventListener("touchmove", handleTouchEnd);
        window.addEventListener("touchend", handleTouchEnd);
        window.addEventListener("touchcancel", handleTouchEnd);

        return () => {
            window.removeEventListener("touchmove", handleTouchEnd);
            window.removeEventListener("touchend", handleTouchEnd);
            window.removeEventListener("touchcancel", handleTouchEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const color = overviewData.value.dateColors[date.getDate() - 1];
    const isSelected =
        mode.value === "day" &&
        selectedDay.value &&
        isSameDay(selectedDay.value, date);

    return (
        <button
            type="button"
            style={{ backgroundColor: color }}
            data-grid-cell={date.getDate()}
            className={cn(
                "no-select-button",
                "size-9 rounded-full text-white font-bold flex justify-center items-center mx-auto",
                "transition-[transform,opacity] duration-300",
                mode.value === "day" && isSelected && "scale-125",
                mode.value === "day" && !isSelected && "opacity-60 scale-90",
                isSameDay(today.value, date) &&
                    "ring-sky-500 ring ring-offset-1 ring-offset-white",
            )}
            onTouchStart={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const container = e.currentTarget.closest<HTMLElement>(
                    "[data-widget-container]",
                );
                const position = getPopupPosition(rect, container);

                if (isPopup.peek()) {
                    onLongPress(position);
                } else {
                    clickIntent.current = true;
                    longPress(() => onLongPress(position));
                }
            }}
            onTouchEnd={(e) => {
                e.preventDefault();
                longPress(false);

                if (clickIntent.current) {
                    mode.value = "day";
                    selectedDay.value = date;
                    mobile.impact("light");
                }
            }}
        >
            <div>{date.getDate()}</div>
        </button>
    );
};

const Wrapper: FC<GridCellProps> = (props) => {
    const { date } = props;
    const { selectedDate } = useMobileDashboard();
    const disabled = !isSameMonth(date, selectedDate.value);

    return <div>{!disabled && <GridCell {...props} />}</div>;
};

export default Wrapper;
